import { useState } from 'react';
import { Crosshair } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { Check, PrintedTarget, describeTarget } from '@/reports/bundles/expectations';
import { Button } from '../../ui/button';
import { CheckEditor } from './CheckEditor';
import { baseName } from './designerChrome';

export type PickScope = 'fixture' | 'rules';

/** Above the preview while picking: what to click, and the way out. */
export function PickBanner({ onDone }: { onDone: () => void }) {
  return (
    <div role="status" className="flex shrink-0 items-center gap-2 border-b border-sky-200 bg-sky-50 px-3 py-1.5 text-xs text-sky-950">
      <Crosshair className="size-3.5 shrink-0" />
      <span className="min-w-0 flex-1"><b>{I18n.get('pickPicking')}</b> {I18n.get('pickHelp')}</span>
      <Button variant="outline" size="xs" onClick={onDone}>{I18n.get('pickDone')}</Button>
    </div>
  );
}

/**
 * What was picked in the preview, written as a check: what it must print
 * there, and whether it holds this fixture or every one (a rules file).
 */
export function PickCard({ target, found, fixture, rulesFile, locked, onAdd, onCancel }: {
  target: PrintedTarget;
  found: string[];
  /** the fixture the preview reads, when it reads one */
  fixture: string | null;
  /** the rules file a check for every fixture goes in */
  rulesFile: string;
  /** the files holding a check they cannot read: fixed in code before one is added to them */
  locked?: { fixture: boolean; rules: boolean };
  onAdd: (check: Check, scope: PickScope) => void;
  onCancel: () => void;
}) {
  const [scope, setScope] = useState<PickScope>(fixture ? 'fixture' : 'rules');
  return (
    <section aria-label={I18n.get('pickCard')} className="flex flex-col gap-2 text-sm">
      <p className="font-medium [overflow-wrap:anywhere]">{describeTarget(target)}</p>
      <fieldset className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <legend className="float-none mb-1 w-auto p-0 text-xs text-muted-foreground">{I18n.get('pickExpectIn')}</legend>
        <label className="flex items-center gap-1.5">
          <input type="radio" name="pick-scope" checked={scope === 'fixture'} disabled={!fixture} onChange={() => setScope('fixture')} />
          {fixture ? I18n.get('pickThisFixture', { file: baseName(fixture) }) : I18n.get('pickNoFixture')}
        </label>
        <label className="flex items-center gap-1.5">
          <input type="radio" name="pick-scope" checked={scope === 'rules'} onChange={() => setScope('rules')} />
          {I18n.get('pickEveryFixture', { file: rulesFile })}
        </label>
      </fieldset>
      {locked?.[scope] && <p role="alert" className="text-xs text-destructive">{I18n.get('pickLocked', { file: scope === 'fixture' && fixture ? baseName(fixture) : rulesFile })}</p>}
      {/* a new pick starts afresh, also one with the same target: the value it starts from is the cell clicked */}
      <CheckEditor key={`${JSON.stringify(target)}|${found[0] ?? ''}`} initial={target} found={found} onCancel={onCancel} onAdd={(check) => onAdd(check, scope)} addLabel={I18n.get('pickAdd')} disabled={!!locked?.[scope]} />
    </section>
  );
}
